// Nigeria — Ayò Ọlọ́pọ́n (Ayoayo, relay) engine.
//
// Board: 2 rows × 6 pits. 48 seeds total (4 per pit).
//   row 0 = Player B (top), row 1 = Player A (bottom).
// Sowing direction: CCW around BOTH rows, starting from the pit immediately
// following the selected pit.
//
// Rules implemented (deterministic Yoruba relay variant per /app/docs/RULESETS_DESIGN.md):
//   - 12-counter skip: on a single sowing pass of ≥12 seeds, the starting
//     pit is skipped when the route returns to it.
//   - Relay sowing: if the final seed lands in an OCCUPIED pit on EITHER row,
//     pick that pit up and continue sowing CCW. Relay terminates only when
//     the final seed lands in a previously-empty pit.
//   - Capture: only when the final seed lands in an EMPTY pit on the
//     active player's OWN row AND the opposite enemy pit is occupied.
//     Capture is single-pit (just the opposite pit). Capturing player does
//     NOT play again (turn passes).
//   - Feeding rule: when the opponent's row is empty, the active player must
//     play a move that feeds at least one seed into the opponent's row when
//     such a move exists.
//   - End: when the active player has no legal move, remaining seeds on the
//     board are awarded to the player on whose row they sit; highest capture
//     total wins.

export const RULES_VERSION = "2026.02.18";
export const RULESET_ID = "ng-ayoayo-relay";
export const ROWS = 2;
export const COLS = 6;
export const PLAYER_A = "A";
export const PLAYER_B = "B";

// CCW route over both rows. Player A's row goes left→right; turning at the
// right edge to Player B's row right→left.
const ROUTE = [
  [1, 0], [1, 1], [1, 2], [1, 3], [1, 4], [1, 5],
  [0, 5], [0, 4], [0, 3], [0, 2], [0, 1], [0, 0],
];

function routeIndex(r, c) {
  for (let i = 0; i < ROUTE.length; i++) if (ROUTE[i][0] === r && ROUTE[i][1] === c) return i;
  return -1;
}
function ownsRow(p, row) { return p === PLAYER_A ? row === 1 : row === 0; }
function oppRowOf(p) { return p === PLAYER_A ? 0 : 1; }
function ownRowOf(p) { return p === PLAYER_A ? 1 : 0; }
function emptyBoard() { return Array.from({ length: ROWS }, () => Array(COLS).fill(0)); }
function cloneBoard(b) { return b.map((r) => r.slice()); }

export function createInitialState() {
  const board = emptyBoard();
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) board[r][c] = 4;
  return {
    rulesetId: RULESET_ID,
    rulesVersion: RULES_VERSION,
    board,
    currentPlayer: PLAYER_A,
    phase: "NORMAL",
    captures: { A: 0, B: 0 },
    status: "ACTIVE",
    winner: null,
    endReason: null,
    turnNumber: 1,
    moveNumber: 0,
    history: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function countersOnRowOf(board, p) {
  const row = ownRowOf(p);
  let s = 0; for (let c = 0; c < COLS; c++) s += board[row][c]; return s;
}

function ownNonEmptyPits(board, p) {
  const row = ownRowOf(p);
  const out = [];
  for (let c = 0; c < COLS; c++) if (board[row][c] > 0) out.push([row, c]);
  return out;
}

// Single sowing pass from a pit (no relay). Implements the 12-counter
// start-pit skip. Returns { board, landing, sownCount }.
function sowOnce(board, startR, startC) {
  const b = cloneBoard(board);
  const count = b[startR][startC];
  b[startR][startC] = 0;
  const startIdx = routeIndex(startR, startC);
  let idx = startIdx;
  let remaining = count;
  let last = [startR, startC];
  while (remaining > 0) {
    idx = (idx + 1) % ROUTE.length;
    if (count >= 12 && idx === startIdx) continue;
    const [nr, nc] = ROUTE[idx];
    b[nr][nc] += 1;
    last = [nr, nc];
    remaining -= 1;
  }
  return { board: b, landing: last, sownCount: count };
}

// Full move sowing with relay. Returns { board, landing, relays }.
function sowAndRelay(board, startR, startC) {
  let b = board;
  let r = startR;
  let c = startC;
  let relays = 0;
  let safety = 0;
  // First sowing pass uses original start pit.
  let first = sowOnce(b, r, c);
  b = first.board; const land0 = first.landing;
  let last = land0;
  while (b[last[0]][last[1]] > 1) {
    // Relay: lift this pit and continue.
    const next = sowOnce(b, last[0], last[1]);
    b = next.board;
    last = next.landing;
    relays += 1;
    if (++safety > 100) break;
  }
  return { board: b, landing: last, relays };
}

// Feeding-aware legal moves.
export function getLegalMoves(state) {
  const { board, currentPlayer } = state;
  const opp = currentPlayer === PLAYER_A ? PLAYER_B : PLAYER_A;
  const all = ownNonEmptyPits(board, currentPlayer);
  if (all.length === 0) return [];
  const oppSeeds = countersOnRowOf(board, opp);
  if (oppSeeds > 0) return all;
  const feed = all.filter(([r, c]) => {
    const { board: post } = sowAndRelay(board, r, c);
    return countersOnRowOf(post, opp) > 0;
  });
  return feed.length > 0 ? feed : all;
}

export function isLegalMove(state, r, c) {
  return getLegalMoves(state).some(([rr, cc]) => rr === r && cc === c);
}

export function applyMove(state, r, c) {
  if (state.status !== "ACTIVE") throw new Error("GAME_NOT_ACTIVE");
  if (!ownsRow(state.currentPlayer, r)) throw new Error("NOT_OWN_PIT");
  if (!isLegalMove(state, r, c)) throw new Error("ILLEGAL_MOVE");

  const player = state.currentPlayer;
  const opp = player === PLAYER_A ? PLAYER_B : PLAYER_A;

  const events = [];
  events.push({ type: "TURN_STARTED", player, turn: state.turnNumber });
  events.push({ type: "PIT_SELECTED", from: [r, c], count: state.board[r][c] });

  const { board: postBoard, landing, relays } = sowAndRelay(state.board, r, c);
  events.push({ type: "SOWING_COMPLETE", landing, relays });

  let board = postBoard;
  let captured = 0;
  const capturedPits = [];
  const [lr, lc] = landing;

  // Capture: empty landing on OWN row AND opposite pit occupied.
  if (lr === ownRowOf(player) && board[lr][lc] === 1) {
    const oppRow = oppRowOf(player);
    if (board[oppRow][lc] > 0) {
      board = cloneBoard(board);
      const n = board[oppRow][lc];
      board[oppRow][lc] = 0;
      captured = n;
      capturedPits.push([oppRow, lc]);
      events.push({ type: "CAPTURE_TRIGGERED", at: landing });
      events.push({ type: "COUNTERS_CAPTURED", from: [oppRow, lc], count: n });
    }
  }

  const captures = { A: state.captures.A, B: state.captures.B };
  captures[player] += captured;

  let status = "ACTIVE";
  let winner = null;
  let endReason = null;

  // Check opponent's legal moves
  const probe = { ...state, board, currentPlayer: opp, captures };
  const oppLegal = getLegalMoves(probe);
  if (oppLegal.length === 0) {
    // Award remaining seeds to the player on whose row they sit, then compare.
    const aLeft = countersOnRowOf(board, PLAYER_A);
    const bLeft = countersOnRowOf(board, PLAYER_B);
    captures.A += aLeft;
    captures.B += bLeft;
    board = emptyBoard();
    events.push({ type: "REMAINING_SEEDS_AWARDED", A: aLeft, B: bLeft });
    if (captures.A === captures.B) {
      status = "DRAWN"; endReason = "DRAW_BY_NO_MOVE";
      events.push({ type: "GAME_DRAWN", reason: endReason });
    } else {
      status = "WON";
      winner = captures.A > captures.B ? PLAYER_A : PLAYER_B;
      endReason = "OPPONENT_NO_LEGAL_MOVE";
      events.push({ type: "GAME_WON", player: winner, reason: endReason });
    }
  }

  events.push({ type: "TURN_ENDED", player, landingPit: landing, captured, relays });

  const historyEntry = {
    player, from: [r, c], landingPit: landing, captured, capturedPits, relays,
    prevBoard: state.board, prevCaptures: state.captures, prevPhase: state.phase,
    prevCurrentPlayer: state.currentPlayer, prevStatus: state.status, prevWinner: state.winner,
    prevEndReason: state.endReason, prevTurnNumber: state.turnNumber, events,
  };

  return {
    state: {
      ...state,
      board,
      currentPlayer: status === "ACTIVE" ? opp : state.currentPlayer,
      captures, status, winner, endReason,
      turnNumber: status === "ACTIVE" ? state.turnNumber + 1 : state.turnNumber,
      moveNumber: state.moveNumber + 1,
      history: [...state.history, historyEntry],
      updatedAt: Date.now(),
    },
    events,
  };
}

export function undoMove(state) {
  if (!state.history.length) return state;
  const last = state.history[state.history.length - 1];
  return {
    ...state,
    board: last.prevBoard,
    currentPlayer: last.prevCurrentPlayer,
    phase: last.prevPhase,
    captures: last.prevCaptures,
    status: last.prevStatus,
    winner: last.prevWinner,
    endReason: last.prevEndReason,
    turnNumber: last.prevTurnNumber,
    moveNumber: Math.max(0, state.moveNumber - 1),
    history: state.history.slice(0, -1),
    updatedAt: Date.now(),
  };
}

export function totalCounters(state) {
  let s = state.captures.A + state.captures.B;
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) s += state.board[r][c];
  return s;
}

export function describeMove(entry) {
  if (!entry) return "";
  const p = entry.player === PLAYER_A ? "Player 1" : "Player 2";
  const parts = [`${p} sowed counter-clockwise from pit ${entry.from[1] + 1}.`];
  if (entry.relays > 0) parts.push(`Relayed ${entry.relays} time${entry.relays === 1 ? "" : "s"}.`);
  if (entry.captured > 0) parts.push(`Captured ${entry.captured} seed${entry.captured === 1 ? "" : "s"} from the opposite pit.`);
  else parts.push("No capture.");
  return parts.join(" ");
}
